/* eslint-disable react-hooks/exhaustive-deps */
import TabNav from "components/TabNav";
import { useCallback, useEffect, useState } from "react";

import { useHasRole } from "contexts/AuthContext";
import { useParams } from "react-router-dom";
import {
  addSummon,
  editComplaint,
  editInvestigation,
  editSummon,
  fetchAccusedSummonByComplaintId,
  fetchAdvocateComplaintById,
  fetchAppealByComplaintId,
  fetchCouncilLitigationBySummonId,
  fetchInvestigationByComplaintId,
  fetchLitigationBySummonId,
  fetchSummonByInvestigationId,
} from "services/advocateComplaintService";
import {
  AccusedSummon,
  AdvocateComplaints,
  Appeal,
  Complaints,
  CouncilLitigation,
  Investigation,
  Litigation,
  Summon,
} from "types";
import {
  ADVOCATE_APPEAL_FEEDBACK_TYPE,
  ADVOCATE_INVESTIGATION_STATUS,
  ADVOCATE_SUMMON_STATUS,
  COMPLIANT_STATUS,
  FEEDBACK_TYPE,
} from "utils/constantValues";
// import { ADVOCATE_ADVOCATE_ROLES } from "utils/ADVOCATE_ROLES";
import ConfirmationModal from "components/ConfirmationModal";
import { useToast } from "contexts/ToastContext";
import AssignedProsecutorHistory from "pages/complaintRequest/fragments/AssignedProsecutorHistory";
import { ADVOCATE_ROLES, ROLES } from "utils/roles";
import { useTranslation } from "contexts/i18nContext";
import ActivityLogs from "./fragments/ActivityLogs";
import AddAccusedSummonModal from "./fragments/AddAccusedSummonModal";
import AddAppealModal from "./fragments/AddAppealModal";
import AddChargeBackFeedbackModal from "./fragments/AddChargeBackFeedbackModal";
import AddChargeDocModal from "./fragments/AddChargeDocModal";
import AddCouncilLitigationDecisionModal from "./fragments/AddCouncilLitigationDecisionModal";
import AddDailyHearingModal from "./fragments/AddDailyHearingModal";
import AddLetterModal from "./fragments/AddLetterModal";
import AddLitigationDecisionModal from "./fragments/AddLitigationDecisionModal";
import AddSummonModal from "./fragments/AddSummonModal";
import AppealCard from "./fragments/AppealCard";
import ChargeDocumentCard from "./fragments/ChargeDocumentCard";
import ComplaintDetailInfoCard from "./fragments/ComplaintDetailInfoCard";
import CouncilLitigationCard from "./fragments/CouncilLitigationCard";
import DecisionCard from "./fragments/DecisionCard";
import ListEvidenceCard from "./fragments/ListEvidenceCard";
import ListReporterCard from "./fragments/ListReporterCard";
import ListVictimCard from "./fragments/ListVictimCard";
import ListWitnessCard from "./fragments/ListWitnessCard";
import LitigationCard from "./fragments/LitigationCard";
import SummonCard from "./fragments/SummonCard";
import SummonConfirmationStatus from "./fragments/SummonConfirmationStatus";
import TerminateLitigationModal from "./fragments/TerminateLitigationModal";

const calculate_remaining_days = ({ date }: { date: string }) => {
  const appeal = new Date(date ?? "");
  const today = new Date();

  // Zero out time to compare dates only
  appeal.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const msPerDay = 1000 * 60 * 60 * 24;
  const diffInMs = appeal.getTime() - today.getTime();

  const remainingDays = Math.floor(diffInMs / msPerDay);

  return remainingDays;
};

const tabs = ["Detail Information", "Litigation", "Appeal"];

export default function SingleAdvocateComplaint() {
  const { t } = useTranslation();
  const { uu_id } = useParams();
  const { showToast } = useToast();

  const canCreateChargeDoc = useHasRole([ADVOCATE_ROLES.INSPECTION_PROSECUTOR]);
  const canApproveLetterCreation = useHasRole([
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
  ]);
  const canCreateLetter = useHasRole([
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.ZONAL_OFFICE_OF_COUNCIL_COORDINATOR,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canCreateSummon = useHasRole([
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
  ]);
  const canIssuedSummon = useHasRole([
    ADVOCATE_ROLES.INSPECTION_REGISTRAR,
    ROLES.INSPECTION_REGISTRAR,
  ]);
  const canSendInvestigationToCouncil = useHasRole([
    ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
  ]);
  const canSendInvestigationToCouncilRegistrar = useHasRole([
    ADVOCATE_ROLES.INSPECTION_REGISTRAR,
    ROLES.INSPECTION_REGISTRAR,
  ]);
  const canViewSummon = useHasRole([
    ADVOCATE_ROLES.INSPECTION_DIRECTOR,
    ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
    ADVOCATE_ROLES.INSPECTION_REGISTRAR,
    ROLES.INSPECTION_REGISTRAR,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.ADVOCATE_ZONAL_OFFICE_OF_COUNCIL_HEAD,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.ZONAL_OFFICE_OF_COUNCIL_COORDINATOR,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canViewSummonCoordinator = useHasRole([
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
  ]);
  const canViewActivityLogs = useHasRole([
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
    ADVOCATE_ROLES.INSPECTION_DIRECTOR,
  ]);
  // const isRegistrar = useHasRole([
  //   ADVOCATE_ROLES.INSPECTION_REGISTRAR,
  //   ROLES.INSPECTION_REGISTRAR,
  // ]);
  const canSendInvestigationToCouncilHead = useHasRole([
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.ZONAL_OFFICE_OF_COUNCIL_COORDINATOR,
  ]);
  const isRegionalCouncilOfficeHead = useHasRole([
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
  ]);
  const canCreateInspectionDailyHearing = useHasRole([
    ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
  ]);
  const canCreateCouncilDailyHearing = useHasRole([
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.ZONAL_OFFICE_OF_COUNCIL_COORDINATOR,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canTerminateComplaint = useHasRole([
    ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
  ]);
  // const canTerminateInspectionLitigation = useHasRole([
  //   ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
  // ]);
  // const canTerminateCouncilLitigation = useHasRole([
  //   ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
  //   ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
  //   ADVOCATE_ROLES.ADVOCATE_ZONAL_OFFICE_OF_COUNCIL_HEAD,
  // ]);
  const canCreateLitigationDecision = useHasRole([
    ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
  ]);
  const canCreateCouncilLitigationDecision = useHasRole([
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.ZONAL_OFFICE_OF_COUNCIL_COORDINATOR,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canAccusedAppeal = useHasRole([
    ADVOCATE_ROLES.INSPECTION_REGISTRAR,
    ROLES.INSPECTION_REGISTRAR,
  ]);
  const canViewCompliantDetail = useHasRole([
    ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
    ADVOCATE_ROLES.INSPECTION_DIRECTOR,
  ]);
  const canViewSummonConfirmation = useHasRole([
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_HEAD,
    ADVOCATE_ROLES.ADVOCATE_ZONAL_OFFICE_OF_COUNCIL_HEAD,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.ZONAL_OFFICE_OF_COUNCIL_COORDINATOR,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
  ]);
  const canViewAssignedHistory = useHasRole([
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
    ADVOCATE_ROLES.INSPECTION_DIRECTOR,
  ]);
  const canRequestAppeal = useHasRole([ADVOCATE_ROLES.INSPECTION_PROSECUTOR]);
  const canViewChargeDoc = useHasRole([
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
    ADVOCATE_ROLES.INSPECTION_DIRECTOR,
    ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
    ADVOCATE_ROLES.INSPECTION_REGISTRAR,
    ROLES.INSPECTION_REGISTRAR,
    ADVOCATE_ROLES.ZONAL_COUNCIL_OFFICE_WRITER,
    ADVOCATE_ROLES.REGIONAL_COUNCIL_OFFICE_WRITER,
  ]);

  const [complaintData, setComplaintData] = useState<
    AdvocateComplaints | AdvocateComplaints[]
  >();
  const [investigationData, setInvestigationData] = useState<Investigation>();
  const [allInvestigationData, setAllInvestigationData] =
    useState<Investigation[]>();
  const [summonData, setSummonData] = useState<Summon>();
  const [litigationData, setLitigationData] = useState<Litigation[]>();
  const [councilLitigationData, setCouncilLitigationData] =
    useState<CouncilLitigation[]>();
  const [appealData, setAppealData] = useState<Appeal[]>();
  const [accusedSummonData, setAccusedSummonData] = useState<AccusedSummon>();

  const [activeTab, setActiveTab] = useState<string>(tabs[0]);
  const [isChargeDocOpen, setIsChargeDocOpen] = useState(false);
  const [isSummonOpen, setIsSummonOpen] = useState(false);
  const [isSummonIssueOpen, setIsSummonIssueOpen] = useState(false);
  const [isApproveCreateLetterOpen, setIsApproveCreateLetterOpen] =
    useState(false);
  const [isLetterOpen, setIsLetterOpen] = useState(false);
  const [isDailyHearingOpen, setIsDailyHearingOpen] = useState(false);
  const [isAppealFormOpen, setIsAppealFormOpen] = useState(false);
  const [isTerminateOpen, setIsTerminateOpen] = useState(false);
  const [isLitigationDecisionOpen, setIsLitigationDecisionOpen] =
    useState(false);
  const [isCouncilLitigationDecisionOpen, setIsCouncilLitigationDecisionOpen] =
    useState(false);
  const [isSendToCouncilModal, setIsSendToCouncilModal] =
    useState<boolean>(false);
  const [isBackToProsecutorModal, setIsBackToProsecutorModal] =
    useState<boolean>(false);
  const [isAccusedSummonOpen, setIsAccusedSummonOpen] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  // const [loading, setLoading] = useState(false);
  // const [error, setError] = useState("");

  const loadComplaints = useCallback(async () => {
    if (!uu_id) return;
    // setLoading(true);
    try {
      const resData = await fetchAdvocateComplaintById(uu_id);
      setComplaintData(
        Array.isArray(resData) ? resData?.slice()?.reverse()[0] : resData,
      );
      // setError("");
    } catch (err) {
      // setError("Failed to load complaint.");
    } finally {
      // setLoading(false);
    }
  }, [uu_id]);

  const loadInvestigations = useCallback(async () => {
    if (!complaintData || !uu_id) return;
    // setLoading(true);
    try {
      const resData = await fetchInvestigationByComplaintId(uu_id);
      if (resData) {
        setAllInvestigationData(resData?.slice()?.reverse());
        setInvestigationData(
          Array.isArray(resData) ? resData?.slice()?.reverse()[0] : resData,
        );
        // setError("");
      }
    } catch (err) {
      // setError("Failed to load investigation.");
    } finally {
      // setLoading(false);
    }
  }, [complaintData]);

  const loadSummon = useCallback(async () => {
    if (!investigationData) return;
    // setLoading(true);
    try {
      const resData = await fetchSummonByInvestigationId(
        investigationData?.uu_id || "",
      );
      if (resData)
        setSummonData(
          Array.isArray(resData) ? resData?.slice()?.reverse()[0] : resData,
        );
      // setError("");
    } catch (err) {
      // setError("Failed to load summon.");
    } finally {
      // setLoading(false);
    }
  }, [investigationData]);

  const loadLitigation = useCallback(async () => {
    if (!complaintData || !summonData) return;
    // setLoading(true);
    try {
      const resData = await fetchLitigationBySummonId(summonData?.id || "");
      if (resData)
        setLitigationData(
          resData?.sort(
            (a, b) =>
              new Date(b?.createdAt ?? "").getTime() -
              new Date(a?.createdAt ?? "").getTime(),
          ),
        );
      // setError("");
    } catch (err) {
      // setError("Failed to load summon.");
    } finally {
      // setLoading(false);
    }
  }, [complaintData, summonData]);

  const loadCouncilLitigation = useCallback(async () => {
    if (!summonData) return;
    // setLoading(true);
    try {
      const resData = await fetchCouncilLitigationBySummonId(
        summonData?.id || "",
      );
      if (resData) setCouncilLitigationData(resData?.slice()?.reverse());
      // setError("");
    } catch (err) {
      // setError("Failed to load summon.");
    } finally {
      // setLoading(false);
    }
  }, [summonData]);

  const loadAppeal = useCallback(async () => {
    if (!complaintData || !uu_id) return;
    // setLoading(true);
    try {
      const resData = await fetchAppealByComplaintId(uu_id);
      if (resData) {
        setAppealData(
          resData?.sort(
            (a, b) =>
              new Date(b?.createdAt ?? "").getTime() -
              new Date(a?.createdAt ?? "").getTime(),
          ),
        );
        // setError("");
      }
    } catch (err) {
      // setError("Failed to load investigation.");
    } finally {
      // setLoading(false);
    }
  }, [complaintData]);

  const loadAccusedSummon = useCallback(async () => {
    if (!complaintData || !uu_id) return;
    // setLoading(true);
    try {
      const resData = await fetchAccusedSummonByComplaintId(uu_id);
      if (resData) {
        setAccusedSummonData(
          resData
            ?.slice()
            ?.reverse()
            ?.filter((obj) => obj.discipline_complain === uu_id)[0],
        );
        // setError("");
      }
    } catch (err) {
      // setError("Failed to load investigation.");
    } finally {
      // setLoading(false);
    }
  }, [complaintData]);

  useEffect(() => {
    loadComplaints();
  }, [uu_id]);

  useEffect(() => {
    loadInvestigations();
  }, [complaintData]);

  useEffect(() => {
    loadSummon();
  }, [investigationData]);

  useEffect(() => {
    loadLitigation();
  }, [complaintData, summonData]);

  useEffect(() => {
    loadCouncilLitigation();
  }, [complaintData, summonData, litigationData]);

  useEffect(() => {
    loadAppeal();
  }, [complaintData, litigationData, councilLitigationData]);

  useEffect(() => {
    loadAccusedSummon();
  }, [complaintData, councilLitigationData]);

  // Accordion state for sidebar sections
  type SectionKey = "evidence" | "reporter" | "victim" | "witness";
  const [accordionOpen, setAccordionOpen] = useState<
    Record<SectionKey, boolean>
  >({
    evidence: false,
    reporter: false,
    victim: false,
    witness: false,
  });

  const toggleSection = (key: SectionKey) =>
    setAccordionOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleSendToCouncilConfirm = async () => {
    if (!isSendToCouncilModal) return;

    if (!investigationData?.uu_id) {
      showToast(t("investigationNotFound"), "error");
      return;
    }

    setIsSubmitLoading(true);

    const data = new FormData();
    if (complaintData && !Array.isArray(complaintData)) {
      data.append("admin_unit_id", complaintData?.admin_unit_id || "");
      data.append("org_id", complaintData?.org_id || "");
      data.append("accused_advocate_name", complaintData?.accused_name || "");
      data.append(
        "user_id_accused_advocate",
        complaintData?.userid_accused_name || "",
      );
      data.append(
        "accused_license_number",
        complaintData?.accused_license_number || "",
      );
      data.append("complaint", String(complaintData.uu_id));
    }

    if (
      investigationData?.status === ADVOCATE_INVESTIGATION_STATUS.ON_CHARGE &&
      !investigationData?.registration_number
    ) {
      data.append(
        "status",
        ADVOCATE_INVESTIGATION_STATUS.FORWARDED_TO_REGISTRAR,
      );
    } else if (
      investigationData?.status ===
      ADVOCATE_INVESTIGATION_STATUS.FORWARDED_TO_REGISTRAR
    ) {
      data.append("status", ADVOCATE_INVESTIGATION_STATUS.FORWARDED_TO_COUNCIL);
    } else if (
      investigationData?.status === ADVOCATE_INVESTIGATION_STATUS.ON_CHARGE &&
      investigationData?.registration_number
    ) {
      data.append("status", ADVOCATE_INVESTIGATION_STATUS.FORWARDED_TO_COUNCIL);
    } else if (
      investigationData?.status ===
      ADVOCATE_INVESTIGATION_STATUS.FORWARDED_TO_COUNCIL
    ) {
      data.append("status", ADVOCATE_INVESTIGATION_STATUS.FORWARDED_TO_HEAD);
    }

    const investigation_id = investigationData?.uu_id;
    const isFormData = true;

    try {
      const resData = await editInvestigation(
        data,
        investigation_id,
        isFormData,
      );

      if (
        resData &&
        investigationData?.status ===
          ADVOCATE_INVESTIGATION_STATUS.FORWARDED_TO_REGISTRAR
      ) {
        await editComplaint({
          ...(complaintData as Complaints),
          has_councile: true,
          status: COMPLIANT_STATUS.COUNCIL_OFFICE,
        });
      }

      loadInvestigations();
      setIsSendToCouncilModal(false);
      showToast(t("sent"), "success");
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setIsSubmitLoading(false);
    }
  };

  const handleApproveLetterCreationSubmit = async () => {
    setIsSubmitLoading(true);

    if (!investigationData?.uu_id) {
      showToast(t("investigationNotFound"), "error");
      return;
    }

    const data = new FormData();
    data.append("status", ADVOCATE_SUMMON_STATUS.FORWARDED_TO_COUNCIL_OFFICE);
    data.append("investigation", investigationData?.uu_id);
    if (complaintData && !Array.isArray(complaintData)) {
      data.append("admin_unit_id", complaintData.admin_unit_id || "");
      data.append("org_id", complaintData.org_id || "");
      data.append("accused_advocate_name", complaintData?.accused_name || "");
      data.append(
        "user_id_accused_advocate",
        complaintData?.userid_accused_name || "",
      );
      data.append(
        "accused_license_number",
        complaintData?.accused_license_number || "",
      );
    }

    const isFormData = true;

    try {
      await addSummon(data, isFormData);

      loadSummon();
      setIsApproveCreateLetterOpen(false);
      showToast(t("successfullyCreated"), "success");
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setIsSubmitLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <h1 className="text-2xl font-semibold text-gray-700 flex items-center gap-2 mb-5">
        <span>💼</span>
        {complaintData && Array.isArray(complaintData)
          ? ""
          : complaintData?.is_on_advocate_complaint
            ? t("advocate")
            : t("paralegal")}{" "}
        {t("complaintDetail")}
      </h1>

      {/* Header: Tabs */}
      <div className="w-full flex flex-wrap items-center justify-between gap-4 mb-5">
        <TabNav active={activeTab} tabs={tabs} onSelect={setActiveTab} />

        <div className="text-right">
          {activeTab === tabs[0] && (
            <div className="flex gap-4">
              {canCreateChargeDoc &&
                (investigationData?.status ===
                  ADVOCATE_INVESTIGATION_STATUS.UNDER_INVESTIGATION ||
                  investigationData?.status ===
                    ADVOCATE_INVESTIGATION_STATUS.BACK_TO_PROSECUTOR ||
                  investigationData?.status ===
                    ADVOCATE_INVESTIGATION_STATUS.BACK_TO_PROSECUTOR_FROM_CORD) &&
                (investigationData?.is_backto_prosecutor
                  ? true
                  : investigationData?.feedback_status ===
                    FEEDBACK_TYPE.APPROVED) &&
                (investigationData?.status ===
                ADVOCATE_INVESTIGATION_STATUS.BACK_TO_PROSECUTOR_FROM_CORD
                  ? true
                  : investigationData?.feedback_status_charge_docu !==
                    FEEDBACK_TYPE.APPROVED) &&
                !Array.isArray(complaintData) &&
                complaintData?.status !== COMPLIANT_STATUS.CLOSED && (
                  <button
                    className="bg-red-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsChargeDocOpen(true)}
                  >
                    {t("uploadChargeDocument")}
                  </button>
                )}

              {((((ADVOCATE_INVESTIGATION_STATUS.FORWARDED_TO_REGISTRAR &&
                canSendInvestigationToCouncilRegistrar) ||
                (canSendInvestigationToCouncil &&
                  !ADVOCATE_INVESTIGATION_STATUS.FORWARDED_TO_REGISTRAR)) &&
                ((investigationData?.status ===
                  ADVOCATE_INVESTIGATION_STATUS.ON_CHARGE &&
                  investigationData?.feedback_status_charge_docu ===
                    FEEDBACK_TYPE.APPROVED &&
                  investigationData?.registration_number) ||
                  (investigationData?.status ===
                    ADVOCATE_INVESTIGATION_STATUS.FORWARDED_TO_REGISTRAR &&
                    investigationData?.registration_number))) ||
                (canSendInvestigationToCouncilHead &&
                  investigationData?.status ===
                    ADVOCATE_INVESTIGATION_STATUS.FORWARDED_TO_COUNCIL)) &&
                !summonData && (
                  <button
                    className="bg-green-600 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsSendToCouncilModal(true)}
                  >
                    {t("sendToCouncilOffice")}
                  </button>
                )}

              {canSendInvestigationToCouncilHead &&
                investigationData?.status ===
                  ADVOCATE_INVESTIGATION_STATUS.FORWARDED_TO_COUNCIL && (
                  <button
                    className="bg-red-600 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsBackToProsecutorModal(true)}
                  >
                    {t("backToProsecutor")}
                  </button>
                )}

              {canApproveLetterCreation &&
                investigationData?.status ===
                  ADVOCATE_INVESTIGATION_STATUS.FORWARDED_TO_HEAD &&
                !summonData && (
                  <button
                    className="bg-green-600 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsApproveCreateLetterOpen(true)}
                  >
                    {t("approvedToCreateLetter")}
                  </button>
                )}

              {canCreateLetter &&
                summonData &&
                summonData?.status ===
                  ADVOCATE_SUMMON_STATUS.FORWARDED_TO_COUNCIL_OFFICE && (
                  <button
                    className="bg-red-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsLetterOpen(true)}
                  >
                    {t("createSummon")}
                  </button>
                )}

              {canCreateSummon &&
                summonData &&
                (summonData?.accused_response ||
                  summonData?.accused_prosecutor_name) &&
                !summonData?.hearing_date && (
                  <button
                    className="bg-red-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsSummonOpen(true)}
                  >
                    {t("createASummon")}
                  </button>
                )}

              {canIssuedSummon &&
                summonData &&
                !summonData?.is_summon_issued && (
                  <button
                    className="bg-red-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsSummonIssueOpen(true)}
                  >
                    {t("issueASummon")}
                  </button>
                )}

              {isRegionalCouncilOfficeHead &&
                investigationData?.status ===
                  ADVOCATE_INVESTIGATION_STATUS.FORWARDED_TO_HEAD &&
                !summonData && (
                  <button
                    className="bg-red-600 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsBackToProsecutorModal(true)}
                  >
                    {t("backToProsecutor")}
                  </button>
                )}

              {canTerminateComplaint &&
                complaintData &&
                !Array.isArray(complaintData) &&
                complaintData.status !== COMPLIANT_STATUS.CLOSED && (
                  <button
                    className="bg-red-900 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsTerminateOpen(true)}
                  >
                    {t("terminate")}
                  </button>
                )}
            </div>
          )}

          {activeTab === tabs[1] && (
            <div className="flex gap-4">
              {complaintData &&
                !Array.isArray(complaintData) &&
                complaintData.status !== COMPLIANT_STATUS.CLOSED &&
                // summonData?.accused_confirmed &&
                summonData?.coordinator_confirmed &&
                ((canCreateInspectionDailyHearing &&
                  (!litigationData ||
                    (litigationData &&
                      !litigationData[0]?.is_final_verdict_given))) ||
                  (canCreateCouncilDailyHearing &&
                    (!councilLitigationData ||
                      (councilLitigationData &&
                        !councilLitigationData[0]
                          ?.is_final_verdict_given)))) && (
                  <button
                    className="bg-red-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsDailyHearingOpen(true)}
                  >
                    {canCreateCouncilDailyHearing &&
                    councilLitigationData?.length === 0
                      ? t("addInitialHearing")
                      : t("addDailyHearing")}
                  </button>
                )}

              {canCreateLitigationDecision &&
                litigationData &&
                litigationData[0]?.is_final_verdict_given &&
                !litigationData[0]?.council_decision_summary &&
                !litigationData[0]?.council_decision && (
                  // councilLitigationData &&
                  // councilLitigationData[0]?.is_final_verdict_given &&
                  // (councilLitigationData[0]?.council_decision_summary ||
                  //   councilLitigationData[0]?.council_decision) &&
                  <button
                    className="bg-red-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsLitigationDecisionOpen(true)}
                  >
                    {t("addLitigationDecision")}
                  </button>
                )}

              {canCreateCouncilLitigationDecision &&
                councilLitigationData &&
                councilLitigationData[0]?.is_final_verdict_given &&
                !councilLitigationData[0]?.council_decision_summary &&
                !councilLitigationData[0]?.council_decision && (
                  <button
                    className="bg-red-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsCouncilLitigationDecisionOpen(true)}
                  >
                    {t("addCouncilOfficeDecision")}
                  </button>
                )}

              {canAccusedAppeal &&
                !accusedSummonData &&
                councilLitigationData &&
                councilLitigationData[0]?.is_final_verdict_given &&
                (councilLitigationData[0]?.council_decision_summary ||
                  councilLitigationData[0]?.council_decision) && (
                  <button
                    className="bg-red-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsAccusedSummonOpen(true)}
                  >
                    {t("createAccusedAppeal")}
                  </button>
                )}

              {canRequestAppeal &&
                litigationData &&
                litigationData[0]?.is_final_verdict_given &&
                !litigationData[0]?.termination_reason &&
                // litigationData[0]?.prosecutor_opinion_on_decision_appeal ===
                //   PROSECUTOR_APPEAL_OPINION.APPEAL &&
                councilLitigationData &&
                calculate_remaining_days({
                  date: councilLitigationData[0]?.council_decision_day ?? "",
                }) > -30 &&
                councilLitigationData[0]?.is_final_verdict_given &&
                (councilLitigationData[0]?.council_decision_summary ||
                  councilLitigationData[0]?.council_decision) &&
                appealData &&
                !appealData[0]?.appeal_status &&
                (appealData?.length <= 0 ||
                  (appealData[0]?.memorandum_summary &&
                    !appealData[0]?.appeal_feedback_status)) && (
                  <button
                    className="bg-red-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsAppealFormOpen(true)}
                  >
                    {t("requestForAppeal")}
                  </button>
                )}
              {canRequestAppeal &&
                litigationData &&
                litigationData[0]?.is_final_verdict_given &&
                !litigationData[0]?.termination_reason &&
                // litigationData[0]?.prosecutor_opinion_on_decision_appeal ===
                //   PROSECUTOR_APPEAL_OPINION.APPEAL &&
                councilLitigationData &&
                calculate_remaining_days({
                  date: councilLitigationData[0]?.council_decision_day ?? "",
                }) > -30 &&
                councilLitigationData[0]?.is_final_verdict_given &&
                (councilLitigationData[0]?.council_decision_summary ||
                  councilLitigationData[0]?.council_decision) &&
                appealData &&
                appealData[0]?.memorandum_summary &&
                appealData[0]?.appeal_feedback_status ===
                  ADVOCATE_APPEAL_FEEDBACK_TYPE.RESPOND_BACK && (
                  <button
                    className="bg-red-700 text-white px-8 py-2 rounded-md hover:bg-gray-800"
                    type="submit"
                    onClick={() => setIsAppealFormOpen(true)}
                  >
                    {t("updateAppeal")}
                  </button>
                )}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Detail Info */}
        {activeTab === tabs[0] && (
          <div className="lg:col-span-2 space-y-4">
            {summonData &&
              (canViewSummon ||
                (canViewSummonCoordinator && summonData?.is_summon_issued)) &&
              investigationData &&
              complaintData &&
              !Array.isArray(complaintData) && (
                <SummonCard
                  complaintData={complaintData}
                  summonData={summonData}
                  compliantData={complaintData}
                  investigationData={investigationData}
                  litigationData={litigationData}
                  onSummonRefresh={loadSummon}
                  onComplaintsRefresh={loadComplaints}
                />
              )}

            {investigationData &&
              complaintData &&
              !Array.isArray(complaintData) &&
              (canViewChargeDoc ||
                investigationData?.status ===
                  ADVOCATE_INVESTIGATION_STATUS.FORWARDED_TO_COUNCIL ||
                investigationData?.status ===
                  ADVOCATE_INVESTIGATION_STATUS.FORWARDED_TO_HEAD) && (
                <ChargeDocumentCard
                  investigationData={investigationData}
                  complaintData={complaintData}
                  onInvestigationRefresh={loadInvestigations}
                  onComplaintRefresh={loadComplaints}
                />
              )}

            {canViewCompliantDetail &&
              complaintData &&
              !Array.isArray(complaintData) &&
              complaintData.status !== COMPLIANT_STATUS?.PENDING && (
                <DecisionCard
                  complaintData={complaintData}
                  investigationData={investigationData}
                  allInvestigationData={allInvestigationData}
                  litigationData={litigationData}
                  onInvestigationRefresh={loadInvestigations}
                  onComplaintsRefresh={loadComplaints}
                />
              )}

            {canViewCompliantDetail && (
              <ComplaintDetailInfoCard
                complaintData={complaintData}
                onComplaintsRefresh={loadComplaints}
              />
            )}
          </div>
        )}

        {/* Litigation */}
        {activeTab === tabs[1] && (
          <div className="lg:col-span-2 space-y-4">
            {litigationData && litigationData?.length > 0 && (
              <LitigationCard
                litigationData={litigationData}
                councilLitigationData={councilLitigationData || []}
                onLitigationRefresh={loadLitigation}
              />
            )}

            {councilLitigationData && councilLitigationData?.length > 0 && (
              <CouncilLitigationCard
                councilLitigationData={councilLitigationData}
                onCouncilLitigationRefresh={loadCouncilLitigation}
              />
            )}
          </div>
        )}

        {/* Appeal */}
        {activeTab === tabs[2] && (
          <div className="lg:col-span-2 space-y-4">
            {complaintData &&
              !Array.isArray(complaintData) &&
              appealData &&
              appealData?.length > 0 && (
                <AppealCard
                  complaintData={complaintData}
                  appealData={appealData}
                  onAppealRefresh={loadAppeal}
                />
              )}
          </div>
        )}

        {/* Sidebar */}
        <div className="space-y-4">
          {canViewActivityLogs &&
            complaintData &&
            !Array.isArray(complaintData) &&
            (investigationData || appealData) && (
              <ActivityLogs
                complaintData={complaintData}
                investigationData={investigationData}
                appealData={
                  appealData && appealData?.length > 0 ? appealData![0] : null
                }
              />
            )}

          {activeTab === tabs[0] &&
            canViewSummonConfirmation &&
            summonData &&
            summonData?.hearing_date && (
              <SummonConfirmationStatus summonData={summonData} />
            )}

          {canViewAssignedHistory &&
            complaintData &&
            !Array.isArray(complaintData) && (
              <AssignedProsecutorHistory
                data={[
                  {
                    assignedTo: complaintData?.assigned_prosecutor,
                    assignedDate: complaintData?.assigned_date,
                  },
                  {
                    assignedTo: litigationData?.[0]?.assigned_prosecutor,
                    assignedDate: new Date(
                      litigationData?.[0]?.createdAt ?? "",
                    )?.toLocaleDateString(),
                  },
                ]}
              />
            )}

          {canViewCompliantDetail && (
            <div className="space-y-2">
              {/* Evidence */}
              <div className="border rounded-lg">
                <button
                  type="button"
                  onClick={() => toggleSection("evidence")}
                  className="w-full flex items-center justify-between px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-t-lg"
                >
                  <span className="text-sm font-semibold text-gray-700">
                    {t("evidence")}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 transition-transform ${
                      accordionOpen.evidence ? "rotate-180" : "rotate-0"
                    }`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {accordionOpen.evidence && (
                  <div className="p-3">
                    <ListEvidenceCard
                      complaintData={complaintData}
                      onComplaintsRefresh={loadComplaints}
                    />
                  </div>
                )}
              </div>

              {/* Reporters */}
              <div className="border rounded-lg">
                <button
                  type="button"
                  onClick={() => toggleSection("reporter")}
                  className="w-full flex items-center justify-between px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-t-lg"
                >
                  <span className="text-sm font-semibold text-gray-700">
                    {t("reporters")}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 transition-transform ${
                      accordionOpen.reporter ? "rotate-180" : "rotate-0"
                    }`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {accordionOpen.reporter && (
                  <div className="p-3">
                    <ListReporterCard
                      complaintData={complaintData}
                      onComplaintsRefresh={loadComplaints}
                    />
                  </div>
                )}
              </div>
              {/* Victims */}
              <div className="border rounded-lg">
                <button
                  type="button"
                  onClick={() => toggleSection("victim")}
                  className="w-full flex items-center justify-between px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-t-lg"
                >
                  <span className="text-sm font-semibold text-gray-700">
                    {t("victims")}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 transition-transform ${
                      accordionOpen.victim ? "rotate-180" : "rotate-0"
                    }`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {accordionOpen.victim && (
                  <div className="p-3">
                    <ListVictimCard
                      complaintData={complaintData}
                      onComplaintsRefresh={loadComplaints}
                    />
                  </div>
                )}
              </div>

              {/* Witnesses */}
              <div className="border rounded-lg">
                <button
                  type="button"
                  onClick={() => toggleSection("witness")}
                  className="w-full flex items-center justify-between px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-t-lg"
                >
                  <span className="text-sm font-semibold text-gray-700">
                    {t("witnesses")}
                  </span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 transition-transform ${
                      accordionOpen.witness ? "rotate-180" : "rotate-0"
                    }`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.23 7.21a.75.75 0 011.06.02L10 10.939l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {accordionOpen.witness && (
                  <div className="p-3">
                    <ListWitnessCard
                      complaintData={complaintData}
                      onComplaintsRefresh={loadComplaints}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* /////////////////// MODAL FORMS ///////////////////  */}
      {complaintData && !Array.isArray(complaintData) && investigationData && (
        <AddChargeDocModal
          isOpen={isChargeDocOpen}
          setIsOpen={setIsChargeDocOpen}
          complaintData={complaintData}
          investigationData={investigationData}
          onInvestigationRefresh={loadInvestigations}
        />
      )}

      {complaintData && !Array.isArray(complaintData) && investigationData && (
        <AddChargeBackFeedbackModal
          isOpen={isBackToProsecutorModal}
          setIsOpen={setIsBackToProsecutorModal}
          complaintData={complaintData}
          investigationData={investigationData}
          onRefresh={loadInvestigations}
        />
      )}

      {investigationData &&
        summonData &&
        complaintData &&
        !Array.isArray(complaintData) && (
          <AddLetterModal
            isOpen={isLetterOpen}
            setIsOpen={setIsLetterOpen}
            summonData={summonData}
            complaintData={complaintData}
            investigationData={investigationData}
            onSummonRefresh={loadInvestigations}
          />
        )}
      {investigationData &&
        summonData &&
        complaintData &&
        !Array.isArray(complaintData) && (
          <AddSummonModal
            isOpen={isSummonOpen}
            setIsOpen={setIsSummonOpen}
            summonData={summonData}
            complaintData={complaintData}
            investigationData={investigationData}
            onSummonRefresh={loadInvestigations}
          />
        )}

      {complaintData &&
        !Array.isArray(complaintData) &&
        summonData &&
        !Array.isArray(summonData) && (
          <AddDailyHearingModal
            isOpen={isDailyHearingOpen}
            setIsOpen={setIsDailyHearingOpen}
            summonData={summonData}
            complaintData={complaintData}
            onLitigationRefresh={loadLitigation}
          />
        )}

      {complaintData && !Array.isArray(complaintData) && (
        <AddAppealModal
          isOpen={isAppealFormOpen}
          setIsOpen={setIsAppealFormOpen}
          complaintData={complaintData}
          onCompliantRefresh={loadComplaints}
          defaultAppealType="inspection_to_council"
        />
      )}

      {litigationData && complaintData && !Array.isArray(complaintData) && (
        <AddLitigationDecisionModal
          isOpen={isLitigationDecisionOpen}
          setIsOpen={setIsLitigationDecisionOpen}
          litigationData={litigationData[0]}
          complaintData={complaintData}
          onRefresh={loadLitigation}
        />
      )}

      {councilLitigationData &&
        complaintData &&
        !Array.isArray(complaintData) && (
          <AddCouncilLitigationDecisionModal
            isOpen={isCouncilLitigationDecisionOpen}
            setIsOpen={setIsCouncilLitigationDecisionOpen}
            councilLitigationData={councilLitigationData[0]}
            complaintData={complaintData}
            onRefresh={loadCouncilLitigation}
          />
        )}

      {complaintData && !Array.isArray(complaintData) && (
        <TerminateLitigationModal
          isOpen={isTerminateOpen}
          setIsOpen={setIsTerminateOpen}
          complaintData={complaintData}
          onRefresh={loadComplaints}
        />
      )}

      {councilLitigationData &&
        complaintData &&
        !Array.isArray(complaintData) && (
          <AddAccusedSummonModal
            isOpen={isAccusedSummonOpen}
            setIsOpen={setIsAccusedSummonOpen}
            complaintData={complaintData}
            onComplaintRefresh={loadComplaints}
          />
        )}

      <ConfirmationModal
        isOpen={isApproveCreateLetterOpen}
        onCancel={() => setIsApproveCreateLetterOpen(false)}
        onConfirm={handleApproveLetterCreationSubmit}
        isLoading={isSubmitLoading}
      />

      <ConfirmationModal
        isOpen={isSendToCouncilModal}
        onCancel={() => setIsSendToCouncilModal(false)}
        onConfirm={handleSendToCouncilConfirm}
        isLoading={isSubmitLoading}
      />

      <ConfirmationModal
        isOpen={isSummonIssueOpen}
        onCancel={() => setIsSummonIssueOpen(false)}
        onConfirm={async () => {
          await editSummon(
            {
              ...summonData,
              is_summon_issued: true,
              status: ADVOCATE_SUMMON_STATUS.SUMMON_ISSUED,
            } as Summon,
            summonData?.id!,
          );

          loadComplaints();
          setIsSummonIssueOpen(false);
          showToast(t("summonIssued"), "success");
        }}
        isLoading={isSubmitLoading}
      />
    </div>
  );
}
